import DayPicker, { DayPickerProps, DayModifiers, Modifier } from "react-day-picker";

import styles from "../../styles/Calendar.module.less";

interface AllModifiers extends DayModifiers {
  previous?: boolean | undefined;
  disabled?: boolean | undefined;
}

// Allow for classnames to be partially defined and extended
type DatePickerProps = Omit<DayPickerProps, "classNames"> & {
  classNames?: Partial<DayPickerProps["classNames"]> & { [key: string]: string };
};

interface CalendarProps extends DatePickerProps {
  onSelect: (day: Date, modifiers: AllModifiers, e: React.MouseEvent<HTMLDivElement>) => void;
  modifiers?: Partial<{ [key in keyof AllModifiers]: Modifier | Modifier[] }>;
}

// Baked into day picker
const defaultModifierClassNames = {
  today: styles.today,
  selected: styles.selected,
  disabled: styles.disabled,
  outside: styles.outside,
};

// Requires custom work
const customModifierClassNames = {
  previous: styles.previous,
};

// All modifiers for easy lookup
const modifierClassNames = {
  ...defaultModifierClassNames,
  ...customModifierClassNames,
};

const defaultClassNames = {
  // Containers
  container: styles.container,
  wrapper: styles.wrapper,

  // Navigation
  interactionDisabled: styles.interactionDisabled,
  navBar: styles.navBar,
  navButtonPrev: `${styles.navButton} ${styles.navButtonPrev}`,
  navButtonNext: `${styles.navButton} ${styles.navButtonNext}`,
  navButtonInteractionDisabled: styles.navButtonInteractionDisabled,

  // Calendar
  months: styles.months,
  month: styles.month,
  caption: styles.caption,
  weekdays: styles.weekdays,
  weekdaysRow: styles.weekdaysRow,
  weekday: styles.weekday,
  weekNumber: styles.weekNumber,
  body: styles.body,
  week: styles.week,
  day: styles.day,
  footer: styles.footer,
  todayButton: styles.todayButton,

  // Modifiers
  ...modifierClassNames,
};

const weekdays = ["S", "M", "T", "W", "T", "F", "S"];

// CSS modules makes this a little tricky since the className is used sa the indentifier under the hood in DayPicker
const makeModifiers = (modifiers: CalendarProps["modifiers"]) => {
  if (!modifiers) {
    return undefined;
  }

  return { [customModifierClassNames.previous]: modifiers.previous };
};

const Calendar = ({ onSelect, classNames, modifiers, ...other }: CalendarProps) => {
  const selectDate = (
    day: Date,
    dayModifiers: DayModifiers,
    e: React.MouseEvent<HTMLDivElement>,
  ) => {
    // Convert modifers from css module classnames to the keys passed in with them
    const mapped: AllModifiers = Object.entries(modifierClassNames).reduce(
      (acc, [key, value]) => ({ ...acc, [key]: !!dayModifiers[value] }),
      { outside: false, today: false },
    );

    // Prevent selecting a disabled date
    if (mapped.disabled) {
      return;
    }

    onSelect(day, mapped, e);
  };

  return (
    <DayPicker
      classNames={{ ...defaultClassNames, ...classNames }}
      weekdaysShort={weekdays}
      onDayClick={selectDate}
      modifiers={makeModifiers(modifiers)}
      {...other}
    />
  );
};

export default Calendar;
